
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from './UserContext';
import { doc, getDoc, db } from '../firebase';

const ADMIN_EMAIL = "yousefkatato66@gmail.com";

interface AdminContextType {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isUnlocked: boolean;
  unlockAdmin: (password: string) => Promise<boolean>;
  lockAdmin: () => void;
  loading: boolean;
  permissions?: any;
  user?: any;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile } = useUser();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      if (user) {
        // Hardcoded super admin OR Firestore-delegated super admin
        const isSuperEmail = user.email === ADMIN_EMAIL;
        const isSuperFromProfile = !!profile?.isSuperAdmin;

        if (isSuperEmail || isSuperFromProfile || profile?.role === 'admin' || (user as any).isAdmin) {
          setIsAdmin(true);
          setIsSuperAdmin(isSuperEmail || isSuperFromProfile);
          const sessionUnlocked = sessionStorage.getItem('admin_unlocked');
          if (sessionUnlocked === 'true') {
            setIsUnlocked(true);
          }
        } else {
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setIsUnlocked(false);
        }
      } else {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsUnlocked(false);
      }
      setLoading(false);
    };
    checkAdmin();
  }, [user, profile]);

  const unlockAdmin = async (password: string) => {
    try {
      // Check against Firestore config in 'adminConfig/access'
      const configRef = doc(db, 'adminConfig', 'access');
      const configSnap = await getDoc(configRef);

      let serverPassword = '';

      if (configSnap.exists()) {
        serverPassword = configSnap.data().adminPassword;
      }

      if (serverPassword && password === serverPassword) {
        setIsUnlocked(true);
        sessionStorage.setItem('admin_unlocked', 'true');
        return true;
      }
      return false;
    } catch (error) {
      console.error("Admin unlock error:", error);
      return false;
    }
  };

  const lockAdmin = () => {
    setIsUnlocked(false);
    sessionStorage.removeItem('admin_unlocked');
  };

  // For super admin: always grant full permissions
  const effectivePermissions = isSuperAdmin
    ? {
      dashboard: { view: true, edit: true },
      products: { view: true, edit: true },
      categories: { view: true, edit: true },
      shops: { view: true, edit: true },
      ads: { view: true, edit: true },
      users: { view: true, edit: true },
      orders: { view: true, edit: true },
      settings: { view: true, edit: true },
      history: { view: true, edit: true },
      sections: { view: true, edit: true }
    }
    : profile?.permissions;

  return (
    <AdminContext.Provider value={{ isAdmin, isSuperAdmin, isUnlocked, unlockAdmin, lockAdmin, loading, permissions: effectivePermissions, user }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
